import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import { formatDisplayDate } from '../../../../../../common/utils/format-utils.js'
import type {
    MyTasksPayload,
    NotionTaskPreview,
    TaskGroup,
} from '../../../../../../server/controllers/portal/dashboard-card-payloads.js'
import { StatusPill } from '../../../../../components/portal-task-pill/portal-task-pill.js'

const Placeholder = ({ text }: { text: string }) => (
    <span className="portal-card__empty">{text}</span>
)

const TaskRow = ({
    task,
    untitled,
    lang,
}: {
    task: NotionTaskPreview
    untitled: string
    lang: Language
}) => {
    const dueLabel = task.dueDate && formatDisplayDate(task.dueDate, 'short', lang)

    const titleContent = (
        <span className="portal-dashboard__tasks-item-title">{task.title || untitled}</span>
    )

    return (
        <li className="portal-dashboard__tasks-item">
            <span className="portal-dashboard__tasks-item-check" />
            <div className="portal-dashboard__tasks-item-body">
                {task.url ? (
                    <a
                        className="portal-dashboard__tasks-item-link"
                        href={task.url}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {titleContent}
                    </a>
                ) : (
                    titleContent
                )}
                <span className="portal-dashboard__tasks-item-meta">
                    <StatusPill status={task.status} />
                    {dueLabel && (
                        <span className="portal-dashboard__tasks-item-due">{dueLabel}</span>
                    )}
                </span>
                {task.clientGoal && (
                    <span className="portal-dashboard__tasks-item-goal">
                        {task.clientGoal.name}
                    </span>
                )}
            </div>
        </li>
    )
}

const Group = ({
    group,
    otherLabel,
    untitled,
    lang,
}: {
    group: TaskGroup
    otherLabel: string
    untitled: string
    lang: Language
}) => (
    <div className="portal-dashboard__tasks-group">
        <h4 className="portal-dashboard__tasks-group-title">
            {group.project ? group.project.name : otherLabel}
        </h4>
        <ul className="portal-dashboard__tasks-list">
            {group.tasks.map((task) => (
                <TaskRow key={task.id} task={task} untitled={untitled} lang={lang} />
            ))}
        </ul>
    </div>
)

export const MyTasksCard = ({ data, lang }: { data: MyTasksPayload; lang: Language }) => {
    const c = t.portal.dashboard.cards.my_tasks

    if (data.status === 'not_configured') {
        return <Placeholder text={c.notConfigured(lang)} />
    }

    if (data.status === 'no_account') {
        return <Placeholder text={c.noAccount(lang)} />
    }

    if (data.status === 'error') {
        return <Placeholder text={c.error(lang)} />
    }

    if (!data.groups || data.groups.length === 0) {
        return <Placeholder text={c.empty(lang)} />
    }

    return (
        <div className="portal-dashboard__tasks-groups">
            {data.groups.map((group) => (
                <Group
                    key={group.project?.id ?? '__other__'}
                    group={group}
                    otherLabel={c.otherGroup(lang)}
                    untitled={c.untitled(lang)}
                    lang={lang}
                />
            ))}
        </div>
    )
}
